import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { storefrontClient } from '../../ShopifyApi/client';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { CUSTOMER_ACCESS_TOKEN_CREATE, CUSTOMER_ORDERS } from '../../ShopifyApi/queries/customer';
import { initializeApp } from 'firebase/app';
import { getAuth, sendPasswordResetEmail, signOut, updateProfile } from 'firebase/auth';
import { auth, functions } from '../../index';


const Settings = () => {
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();


  const getData = async () => {
    const createToken = httpsCallable(functions, 'createToken');
    const token = await createToken({ email: user.email });

    const customerAccessToken = await storefrontClient.request(CUSTOMER_ACCESS_TOKEN_CREATE, {
      multipassToken: token.data.token
    });

    const ordersData = await storefrontClient.request(CUSTOMER_ORDERS, {
      customerAccessToken: customerAccessToken.customerAccessTokenCreateWithMultipass.customerAccessToken.accessToken
    });

    setOrders(ordersData);
  }

  useEffect(() => {
    getData();
  }, []);

  useEffect(() => {
    if (user) {
      setDisplayName(user.displayName || '');
    }
  }, [user]);

  const handleSignOut = async () => {
    try {
      setLoading(true);
      await signOut(auth);
      navigate('/signin');
    } catch (error) {
      setError('Failed to sign out. Please try again.');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = () => {
    setIsPasswordModalOpen(true);
  };

  const confirmPasswordChange = async () => {
    try {
      setLoading(true);
      await sendPasswordResetEmail(auth, user.email);
      setSuccess('Password reset email sent. Check your inbox.');
      setIsPasswordModalOpen(false);
    } catch (error) {
      setError('Failed to send password reset email. Please try again.');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    try {
      setIsUpdatingProfile(true);
      setError('');
      setSuccess('');

      const updates = {};
      if (displayName !== user.displayName) {
        updates.displayName = displayName;
      }

      await updateProfile(auth.currentUser, updates);
      setSuccess('Profile updated successfully!');
    } catch (error) {
      setError('Failed to update profile. Please try again.');
      console.error(error);
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-lg shadow-lg p-6"
      >
        <h1 className="text-3xl font-bold mb-8">Account Settings</h1>

        {error && (
          <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        <div className="space-y-6">
          <div className="border-b pb-6">
            <h2 className="text-xl font-semibold mb-4">Profile Information</h2>
            <form onSubmit={handleProfileUpdate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Display Name
                </label>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  placeholder="Enter your display name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <p className="text-gray-900">{user?.email}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Account Created
                </label>
                <p className="text-gray-900">
                  {user?.metadata.creationTime ? new Date(user.metadata.creationTime).toLocaleDateString() : 'N/A'}
                </p>
              </div>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                className="bg-yellow-500 text-black px-4 py-2 rounded hover:bg-yellow-600 transition-colors duration-300"
                disabled={isUpdatingProfile}
              >
                {isUpdatingProfile ? 'Updating...' : 'Update Profile'}
              </motion.button>
            </form>
          </div>

          <div className="border-b pb-6">
            <h2 className="text-xl font-semibold mb-4">Security</h2>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="bg-yellow-500 text-black px-4 py-2 rounded hover:bg-yellow-600 transition-colors duration-300"
              onClick={handlePasswordChange}
            >
              Change Password
            </motion.button>
          </div>

          <div className="border-b pb-6">
            <h2 className="text-xl font-semibold mb-4">Past Orders</h2>
            <div className="space-y-4">
              {orders?.customer?.orders?.edges?.map((order) => (
                <motion.div
                  key={order.node.id}
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  className="bg-gray-50 p-4 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors duration-200"
                  onClick={() => {
                    setSelectedOrder(order.node);
                    setIsModalOpen(true);
                  }}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium">Order #{order.node.orderNumber}</p>
                      <p className="text-sm text-gray-600">
                        {new Date(order.node.processedAt).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium">
                        ${parseFloat(order.node.totalPrice.amount).toFixed(2)}
                      </p>
                      <p className="text-sm text-gray-600">
                        {order.node.fulfillmentStatus.toLowerCase()}
                      </p>
                    </div>
                  </div>
                  <div className="mt-2">
                    <p className="text-sm text-gray-600">
                      Status: <span className="font-medium">{order.node.financialStatus}</span>
                    </p>
                  </div>
                </motion.div>
              ))}

              {orders?.customer?.orders?.edges?.length === 0 && (
                <p className="text-gray-600">No orders found</p>
              )}
            </div>
          </div>

          <div>
            <h2 className="text-xl font-semibold mb-4">Account Actions</h2>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600 transition-colors duration-300"
              onClick={handleSignOut}
              disabled={loading}
            >
              {loading ? 'Signing Out...' : 'Sign Out'}
            </motion.button>
          </div>
        </div>
      </motion.div>

      {/* Password Change Confirmation Modal */}
      {isPasswordModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white rounded-lg p-6 max-w-md w-full mx-4"
          >
            <h2 className="text-2xl font-bold mb-4">Confirm Password Change</h2>
            <p className="text-gray-600 mb-6">
              A password reset email will be sent to {user?.email}. You will need to click the link in the email to set a new password.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setIsPasswordModalOpen(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors duration-300"
              >
                Cancel
              </button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="bg-yellow-500 text-black px-4 py-2 rounded hover:bg-yellow-600 transition-colors duration-300"
                onClick={confirmPasswordChange}
                disabled={loading}
              >
                {loading ? 'Sending...' : 'Send Reset Email'}
              </motion.button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Success Message */}
      {success && (
        <div className="fixed bottom-4 right-4 bg-green-50 border-l-4 border-green-400 p-4 rounded shadow-lg">
          <p className="text-green-700">{success}</p>
        </div>
      )}

      {/* Order Details Modal */}
      {isModalOpen && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"
          >
            <div className="flex justify-between items-start mb-4">
              <h2 className="text-2xl font-bold">Order Details</h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Order Number</p>
                  <p className="font-medium">#{selectedOrder.orderNumber}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Date</p>
                  <p className="font-medium">
                    {new Date(selectedOrder.processedAt).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Total Amount</p>
                  <p className="font-medium">
                    ${parseFloat(selectedOrder.totalPrice.amount).toFixed(2)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Financial Status</p>
                  <p className="font-medium">{selectedOrder.financialStatus}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Fulfillment Status</p>
                  <p className="font-medium">{selectedOrder.fulfillmentStatus}</p>
                </div>
              </div>

              <div className="mt-6">
                <h3 className="text-lg font-semibold mb-3">Order Items</h3>
                <div className="space-y-4">
                  {selectedOrder.lineItems.edges.map((item) => (
                    <div key={item.node.id} className="flex items-center py-2 border-b">
                      {item.node.variant?.image && (
                        <img 
                          src={item.node.variant.image.url} 
                          alt={item.node.variant.image.altText || item.node.title}
                          className="w-16 h-16 object-contain rounded mr-4"
                        />
                      )}
                      <div className="flex-1">
                        <p className="font-medium">{item.node.title}</p>
                        <p className="text-sm text-gray-600">Quantity: {item.node.quantity}</p>
                      </div>
                      <p className="font-medium ml-4">
                        ${parseFloat(item.node.variant?.price?.amount || item.node.originalUnitPrice?.amount || 0).toFixed(2)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Settings;
